import {
  buildAdjacency,
  computeStats,
  gridSize,
  type IndexedMesh,
  meshGrid,
  type SdfGrid,
} from "./geometry.ts";
import type { Target } from "./targets.ts";

/** Grid spacing of the morph. Finer is smoother but slower to re-mesh while dragging. */
const CELL = 0.06;
/** Empty space kept around both shapes, in cells. */
const MARGIN = 3;
/** Laplacian passes that take the grid's stair-step texture off each frame. */
const SMOOTH_PASSES = 2;

/**
 * "あなた ↔ おてほん" on the result screen. Both shapes are sampled as signed distance
 * fields on one grid; a morph step blends the two fields and re-meshes the blend with
 * surface nets. No vertex correspondence is needed, so it works whatever the clay's mesh
 * has become (remeshing changes its connectivity, and the player may have formed parts
 * from anywhere), and the in-between shapes melt into each other instead of sliding.
 */
export interface MorphFrame extends IndexedMesh {
  normals: Float32Array;
}

export class Morph {
  private readonly clay: Float32Array;
  private readonly target: Float32Array;
  private readonly grid: SdfGrid;

  /**
   * `positions` / `index`: the clay, centred like the target (volume centroid at the
   * origin). Every frame is scaled to `volume`: clay neither appears nor disappears.
   */
  constructor(
    positions: Float32Array,
    index: Uint32Array,
    target: Target,
    private readonly volume: number,
  ) {
    let reach = 0;
    for (const p of [positions, target.positions]) {
      for (let i = 0; i < p.length; i++) reach = Math.max(reach, Math.abs(p[i]));
    }
    const bound = reach + MARGIN * CELL;
    const n = gridSize(bound, CELL);
    this.clay = clayField(positions, index, n, bound, CELL);
    this.target = new Float32Array(n * n * n);
    for (let k = 0, g = 0; k < n; k++) {
      for (let j = 0; j < n; j++) {
        for (let i = 0; i < n; i++, g++) {
          this.target[g] = target.sdf(-bound + i * CELL, -bound + j * CELL, -bound + k * CELL);
        }
      }
    }
    this.grid = { n, bound, cell: CELL, values: new Float32Array(n * n * n) };
  }

  /** The shape at `t` (0 = the clay, 1 = the target). */
  at(t: number): MorphFrame {
    const v = this.grid.values;
    for (let g = 0; g < v.length; g++) v[g] = this.clay[g] + (this.target[g] - this.clay[g]) * t;
    const { positions, index } = meshGrid(this.grid);
    const n = positions.length / 3;
    smooth(positions, index);
    const normals = new Float32Array(n * 3);
    const { volume, centroid: c } = computeStats(positions, index, normals, new Float32Array(n));
    // Blending two distance fields thins the in-between shapes; restore the clay's volume.
    const s = Math.cbrt(this.volume / volume);
    for (let i = 0; i < positions.length; i += 3) {
      positions[i] = c.x + (positions[i] - c.x) * s;
      positions[i + 1] = c.y + (positions[i + 1] - c.y) * s;
      positions[i + 2] = c.z + (positions[i + 2] - c.z) * s;
    }
    return { positions, index, normals };
  }
}

/** Uniform Laplacian smoothing in place. */
function smooth(positions: Float32Array, index: Uint32Array): void {
  const n = positions.length / 3;
  const { offsets, neighbors } = buildAdjacency(index, n);
  const next = new Float32Array(positions.length);
  for (let pass = 0; pass < SMOOTH_PASSES; pass++) {
    for (let v = 0; v < n; v++) {
      const start = offsets[v], end = offsets[v + 1];
      for (let k = 0; k < 3; k++) {
        let sum = 0;
        for (let e = start; e < end; e++) sum += positions[neighbors[e] * 3 + k];
        const avg = end > start ? sum / (end - start) : positions[v * 3 + k];
        next[v * 3 + k] = (positions[v * 3 + k] + avg) / 2;
      }
    }
    positions.set(next);
  }
}

/**
 * Signed distance to the clay at every grid point. Points near a vertex are seeded with
 * their distance to it; the closest vertex is then propagated across the grid by sweeps
 * in all eight diagonal directions (a vector distance transform), so far points get
 * true Euclidean distances too. The sign comes from the closest vertex's normal.
 */
function clayField(
  positions: Float32Array,
  index: Uint32Array,
  n: number,
  bound: number,
  cell: number,
): Float32Array {
  const count = positions.length / 3;
  const normals = new Float32Array(count * 3);
  computeStats(positions, index, normals, new Float32Array(count));
  const total = n * n * n;
  const closest = new Int32Array(total).fill(-1);
  const best = new Float32Array(total).fill(Infinity);
  const at = (i: number) => -bound + i * cell;
  const d2 = (i: number, j: number, k: number, v: number) => {
    const dx = at(i) - positions[v * 3], dy = at(j) - positions[v * 3 + 1];
    const dz = at(k) - positions[v * 3 + 2];
    return dx * dx + dy * dy + dz * dz;
  };

  const R = 2;
  for (let v = 0; v < count; v++) {
    const ci = Math.round((positions[v * 3] + bound) / cell);
    const cj = Math.round((positions[v * 3 + 1] + bound) / cell);
    const ck = Math.round((positions[v * 3 + 2] + bound) / cell);
    for (let k = Math.max(0, ck - R); k <= Math.min(n - 1, ck + R); k++) {
      for (let j = Math.max(0, cj - R); j <= Math.min(n - 1, cj + R); j++) {
        for (let i = Math.max(0, ci - R); i <= Math.min(n - 1, ci + R); i++) {
          const g = (k * n + j) * n + i;
          const d = d2(i, j, k, v);
          if (d < best[g]) {
            best[g] = d;
            closest[g] = v;
          }
        }
      }
    }
  }
  /** Takes over neighbour h's closest vertex at point g = (i, j, k) if it is closer. */
  const adopt = (g: number, h: number, i: number, j: number, k: number) => {
    const v = closest[h];
    if (v < 0 || v === closest[g]) return;
    const d = d2(i, j, k, v);
    if (d < best[g]) {
      best[g] = d;
      closest[g] = v;
    }
  };

  for (let pass = 0; pass < 2; pass++) {
    for (const sz of [1, -1]) {
      for (const sy of [1, -1]) {
        for (const sx of [1, -1]) {
          for (let kk = 0; kk < n; kk++) {
            const k = sz > 0 ? kk : n - 1 - kk;
            for (let jj = 0; jj < n; jj++) {
              const j = sy > 0 ? jj : n - 1 - jj;
              for (let ii = 0; ii < n; ii++) {
                const i = sx > 0 ? ii : n - 1 - ii;
                const g = (k * n + j) * n + i;
                if (i - sx >= 0 && i - sx < n) adopt(g, g - sx, i, j, k);
                if (j - sy >= 0 && j - sy < n) adopt(g, g - sy * n, i, j, k);
                if (k - sz >= 0 && k - sz < n) adopt(g, g - sz * n * n, i, j, k);
              }
            }
          }
        }
      }
    }
  }

  const field = new Float32Array(total);
  for (let k = 0, g = 0; k < n; k++) {
    for (let j = 0; j < n; j++) {
      for (let i = 0; i < n; i++, g++) {
        const v = closest[g];
        const outward = (at(i) - positions[v * 3]) * normals[v * 3] +
          (at(j) - positions[v * 3 + 1]) * normals[v * 3 + 1] +
          (at(k) - positions[v * 3 + 2]) * normals[v * 3 + 2];
        field[g] = outward >= 0 ? Math.sqrt(best[g]) : -Math.sqrt(best[g]);
      }
    }
  }
  return field;
}
